/**
 * How a file looks in the editor: the theme, the syntax scheme, the two
 * lexers CodeMirror does not ship, and the map from a filename to a language.
 *
 * It is a separate module from `EditorPane` because none of it is about the
 * pane. The pane is a `EditorView` with a save key and a file tree; this is
 * what a document looks like once it is open, and keeping the two apart means
 * the colours can be rendered and looked at without a session, an api client
 * or a virtual machine behind them.
 */
import { EditorView } from '@codemirror/view';
import { HighlightStyle, StreamLanguage, type StreamParser } from '@codemirror/language';
import { javascript } from '@codemirror/lang-javascript';
import { markdown } from '@codemirror/lang-markdown';
import { tags as t } from '@lezer/highlight';
import type { Extension } from '@codemirror/state';
import { tango, tangoQuiet } from '../../lib/tango';

const MONO = 'ui-monospace, "Cascadia Code", "JetBrains Mono", "SF Mono", Menlo, Consolas, "Liberation Mono", monospace';

/* ── Theme ────────────────────────────────────────────────────────────────── */

/**
 * The editor is coloured, and it is coloured in Tango — the same sixteen the
 * terminal beside it hands to the guest (`lib/tango.ts`).
 *
 * This reverses an earlier decision. The editor used to code syntax as
 * *brightness*, on the grounds that the product has one hue and spends it on
 * what is live. The argument against that is the tab strip: Terminal, Editor
 * and Pull requests are three views of one machine, and the terminal has
 * always printed the full ANSI ramp because that is what a terminal does. A
 * grey editor next to a coloured terminal did not read as restraint, it read
 * as two different programs. Sharing the ramp settles it — a string is
 * magenta whether the learner is editing the file or `cat`-ing it.
 *
 * What did *not* change is the furniture. Gutters, selection, panels,
 * matching brackets and the caret stay achromatic, because hue here means
 * "this is what the file says". Anything the product itself drew stays grey,
 * and the caret in particular stays white: Signal Green is the terminal
 * cursor's, and the two panes never show a caret at the same time.
 */
export const tangoTheme = EditorView.theme(
  {
    '&': {
      backgroundColor: '#000000',
      color: tango.brightWhite,
      fontFamily: MONO,
      fontSize: '13px',
      height: '100%',
    },
    '.cm-scroller': {
      fontFamily: MONO,
      lineHeight: '1.55',
      overflow: 'auto',
    },
    '.cm-content': {
      caretColor: '#ffffff',
      padding: '8px 0',
    },
    '&.cm-focused': { outline: 'none' },

    '.cm-cursor, .cm-dropCursor': { borderLeftColor: '#ffffff', borderLeftWidth: '2px' },
    // The focused half spells out CodeMirror's own path through the scroller
    // and the selection layer, because its base theme does: the shorter
    // `&.cm-focused .cm-selectionBackground` lost on specificity, so this grey
    // only showed once the editor was blurred, and a live selection was
    // CodeMirror's default teal.
    '&.cm-focused > .cm-scroller > .cm-selectionLayer .cm-selectionBackground, .cm-selectionBackground, .cm-content ::selection': {
      backgroundColor: '#303030',
    },
    // Selected text keeps its own colour. base.css inverts every selection on
    // the page to `--paper`, which is black in here, and CodeMirror clears only
    // the native selection's background — so without this every selected
    // character was painted black on the grey above. It has to be
    // `currentColor`: under highlight inheritance, `inherit`, `unset` and
    // `revert` all resolve to that same black.
    '.cm-line ::selection, .cm-line::selection': { color: 'currentColor' },
    '.cm-selectionMatch': { backgroundColor: '#1f1f1f' },

    '.cm-activeLine': { backgroundColor: '#0d0d0d' },
    '.cm-activeLineGutter': { backgroundColor: '#0d0d0d', color: '#8a8a8a' },

    '.cm-gutters': {
      backgroundColor: '#000000',
      color: '#4a4a4a',
      border: 'none',
      borderRight: '1px solid #1a1a1a',
    },
    '.cm-lineNumbers .cm-gutterElement': { padding: '0 10px 0 8px' },
    '.cm-foldGutter .cm-gutterElement': { color: '#4a4a4a' },

    '.cm-matchingBracket, &.cm-focused .cm-matchingBracket': {
      backgroundColor: '#262626',
      outline: '1px solid #4a4a4a',
      color: '#ffffff',
    },
    '.cm-nonmatchingBracket, &.cm-focused .cm-nonmatchingBracket': { outline: '1px solid #757575' },

    '.cm-searchMatch': { backgroundColor: '#262626', outline: '1px solid #454545' },
    '.cm-searchMatch.cm-searchMatch-selected': { backgroundColor: '#3d3d3d' },

    '.cm-panels': { backgroundColor: '#0a0a0a', color: tango.brightWhite, borderColor: '#202020' },
    '.cm-panels.cm-panels-bottom': { borderTop: '1px solid #202020' },
    '.cm-panels input, .cm-panels button': {
      backgroundColor: '#000000',
      color: tango.brightWhite,
      border: '1px solid #303030',
      fontFamily: MONO,
      fontSize: '12px',
    },

    '.cm-tooltip': { backgroundColor: '#0a0a0a', border: '1px solid #303030', color: tango.brightWhite },
    '.cm-tooltip-autocomplete ul li[aria-selected]': { backgroundColor: '#1f1f1f', color: '#ffffff' },
  },
  { dark: true },
);

/**
 * Tango, assigned by what a token *is* rather than by which language it came
 * from, so the same meaning keeps the same colour in a shell script and in a
 * `.js` file:
 *
 *   keywords, control flow   bright green    the shape of the program
 *   commands, functions      bright blue     the thing being called
 *   types, classes           bright yellow
 *   strings, characters      bright magenta  data, and it reads as data
 *   numbers, booleans, null  bright cyan
 *   names, properties        bright white    the reading weight
 *   operators, flags         white
 *   comments                 aluminium, italic
 *   punctuation              dimmest — scaffolding you read past
 *   broken syntax            bright red, underlined
 *
 * One deliberate overlap: keyword green and the brand's Signal Green are
 * neighbours. They never have to be told apart, because nothing inside a
 * document is ever "live" — the acid only appears on product chrome, and no
 * product chrome is drawn inside this box.
 */
export const tangoHighlight = HighlightStyle.define(
  [
    { tag: [t.keyword, t.controlKeyword, t.moduleKeyword, t.definitionKeyword, t.operatorKeyword], color: tango.brightGreen, fontWeight: '600' },
    { tag: [t.function(t.variableName), t.function(t.propertyName), t.labelName, t.macroName], color: tango.brightBlue },
    { tag: [t.typeName, t.className, t.namespace, t.definition(t.typeName)], color: tango.brightYellow },
    { tag: [t.self, t.null, t.atom, t.bool], color: tango.brightCyan },
    { tag: [t.number, t.integer, t.float, t.unit], color: tango.brightCyan },
    { tag: [t.string, t.special(t.string), t.regexp, t.character], color: tango.brightMagenta },
    { tag: t.escape, color: tango.brightYellow },
    { tag: [t.definition(t.variableName), t.definition(t.propertyName)], color: tango.brightWhite, fontWeight: '600' },
    { tag: [t.variableName, t.propertyName], color: tango.brightWhite },
    { tag: t.attributeName, color: tango.white },
    { tag: [t.operator, t.derefOperator, t.compareOperator, t.arithmeticOperator, t.logicOperator], color: tango.white },
    { tag: t.meta, color: tango.white },
    { tag: [t.comment, t.blockComment, t.lineComment, t.docComment], color: tangoQuiet.comment, fontStyle: 'italic' },
    { tag: [t.punctuation, t.separator, t.bracket, t.paren, t.brace, t.squareBracket, t.angleBracket], color: tangoQuiet.scaffold },
    { tag: t.invalid, color: tango.brightRed, textDecoration: 'underline wavy' },

    // Markdown, where "syntax" is really typography. Prose is read, not
    // parsed, so this half stays nearly achromatic on purpose and spends
    // its colour only on the two things that are machine rather than text:
    // a link's target, and a span of code.
    { tag: [t.heading, t.heading1, t.heading2, t.heading3], color: '#ffffff', fontWeight: '700' },
    { tag: [t.heading4, t.heading5, t.heading6], color: tango.brightWhite, fontWeight: '600' },
    { tag: t.strong, color: '#ffffff', fontWeight: '700' },
    { tag: t.emphasis, color: tango.brightWhite, fontStyle: 'italic' },
    { tag: t.strikethrough, color: tangoQuiet.comment, textDecoration: 'line-through' },
    { tag: [t.link, t.url], color: tango.brightBlue, textDecoration: 'underline' },
    { tag: [t.monospace, t.literal], color: tango.brightCyan },
    { tag: t.quote, color: tangoQuiet.comment, fontStyle: 'italic' },
    { tag: t.list, color: tango.white },
  ],
  { themeType: 'dark' },
);

/* ── Lexers ───────────────────────────────────────────────────────────────── */

/**
 * Two languages the editor needs and CodeMirror does not ship.
 *
 * Counting what the missions actually seed into a workspace: 1414 `.js`,
 * 341 `.sh`, 227 `.json`, 193 `.md`. JavaScript and Markdown have real
 * Lezer grammars in `@codemirror/lang-*`; shell and JSON do not, and adding
 * two dependencies to an offline-first build to colour them would be the
 * wrong trade. Both are small enough to write as `StreamLanguage` tokenisers
 * against `@codemirror/language`, which is already a dependency.
 *
 * Neither is a parser. They are lexers with just enough state to be right
 * about the things that actually break highlighting in the files a learner
 * opens here — heredocs in a seed script, interpolation inside a double
 * quote, a key that is a string but wants to read as a key. Everything else
 * degrades to plain text, which is what the whole file used to be.
 */

/* ── Shell ────────────────────────────────────────────────────────────────── */

/** `if`, `for`, `case` — the shape of the script, not the work it does. */
const SHELL_KEYWORDS = new Set([
  'if', 'then', 'else', 'elif', 'fi', 'for', 'while', 'until', 'do', 'done',
  'case', 'esac', 'in', 'function', 'select', 'time', 'coproc',
]);

/**
 * The keywords a command may follow directly: `if grep …`, `do cp …`.
 *
 * The rest do not open one, and the difference is visible. `for` is followed
 * by a loop variable and `in` by a word list, so a shell that treated every
 * keyword alike would paint the `i` in `for i in …` as if it were a program.
 */
const SHELL_OPENS_COMMAND = new Set(['if', 'elif', 'then', 'else', 'do', 'while', 'until', 'time', 'coproc']);

/**
 * Builtins and the handful of commands that carry control meaning. The list
 * is deliberately short: anything not on it is tagged as a command by
 * *position*, which is how a shell decides too, and which means `git`,
 * `awk` and a mission's own `./deploy.sh` all colour correctly without this
 * file ever having heard of them.
 */
const SHELL_BUILTINS = new Set([
  'echo', 'printf', 'cd', 'pwd', 'export', 'local', 'readonly', 'declare',
  'set', 'unset', 'shift', 'source', 'eval', 'exec', 'exit', 'return',
  'read', 'test', 'trap', 'true', 'false', 'wait', 'kill', 'jobs', 'alias',
  'unalias', 'type', 'hash', 'umask', 'ulimit', 'getopts', 'pushd', 'popd',
  'command', 'builtin', 'let', 'mapfile', 'readarray', 'break', 'continue',
]);

interface ShellState {
  /** The terminator we are looking for, once a heredoc has been opened. */
  heredoc: { tag: string; indented: boolean } | null;
  /** Inside a `"…"` that has not closed yet — they run over line ends. */
  inDouble: boolean;
  /**
   * One entry per open `$(`, holding whether a double quote was interrupted
   * to enter it. `"$(git rev-parse HEAD)"` is a program inside a string, and
   * without this the program would read as string — which is most of the
   * assignments in a real script.
   */
  subst: boolean[];
  /** The next word is in command position, so it names a program. */
  command: boolean;
}

/** Everything that puts the *next* word back in command position. */
function resetsCommand(op: string): boolean {
  return op === '|' || op === '||' || op === '&&' || op === ';' || op === ';;' || op === '&';
}

/** The subset of `StringStream` this file uses, named so the helpers can type. */
type ShellStream = Parameters<StreamParser<ShellState>['token']>[0];

/**
 * The body of a `"…"`: string, except the `$…` inside it, which are not.
 *
 * Called with the opening quote already consumed and returned, so a token
 * never straddles the quote and the name after it.
 */
function tokenDoubleQuoted(stream: ShellStream, state: ShellState): string {
  if (stream.peek() === '$') {
    stream.next();
    if (readVariable(stream)) enterSubstitution(state);
    return 'shDollar';
  }
  while (!stream.eol()) {
    const ch = stream.next() as string;
    if (ch === '\\') {
      stream.next();
      continue;
    }
    if (ch === '"') {
      state.inDouble = false;
      break;
    }
    // Stop short of an interpolation so the next call can colour it.
    if (stream.peek() === '$') break;
  }
  return 'shString';
}

/**
 * Past a `$` that has already been eaten: `VAR`, `{…}`, `(…)`, `1`, `?`.
 *
 * Returns true for `$(`, where what follows is a command substitution and
 * therefore starts a fresh command position — `"$(git rev-parse …)"` has a
 * program in it and should say so.
 */
function readVariable(stream: ShellStream): boolean {
  if (stream.eat('{')) {
    while (!stream.eol() && stream.next() !== '}') {
      /* a `${…}` can hold almost anything; take it whole */
    }
    return false;
  }
  if (stream.eat('(')) return true;
  if (stream.match(/^[A-Za-z_][A-Za-z0-9_]*/)) return false;
  stream.next(); // `$1`, `$?`, `$#`, `$@`, `$$`, `$!`
  return false;
}

/** `$(` opened: suspend any double quote, and start a fresh command. */
function enterSubstitution(state: ShellState): void {
  state.subst.push(state.inDouble);
  state.inDouble = false;
  state.command = true;
}

const shellParser: StreamParser<ShellState> = {
  name: 'shell',
  startState: () => ({ heredoc: null, inDouble: false, subst: [], command: true }),

  token(stream, state) {
    // A heredoc swallows whole lines, terminator included, and it outranks
    // everything else — a `#` inside one is text, not a comment. Seed
    // scripts are mostly heredoc by volume, so getting this wrong would
    // have meant colouring most of a mission's files as if they were code.
    //
    // `sol()` is the whole of the rule that the body starts on the *next*
    // line: without it the opener's own line (`cat <<EOF > out`) would be
    // eaten by the heredoc it just declared.
    if (state.heredoc && stream.sol()) {
      const line = stream.string;
      const candidate = state.heredoc.indented ? line.trim() : line;
      if (candidate === state.heredoc.tag) state.heredoc = null;
      stream.skipToEnd();
      return 'shString';
    }

    if (state.inDouble) return tokenDoubleQuoted(stream, state);

    // A newline ends a command, so the first word of a line names one again.
    // Without this a single comment early in the file leaves every command
    // after it uncoloured, which is exactly what a seed script starts with.
    if (stream.sol()) state.command = true;

    if (stream.eatSpace()) return null;

    // `#!/bin/bash`, and only on the first line, where it means something.
    if (stream.sol() && stream.match(/^#!.*/)) return 'shShebang';

    const ch = stream.peek() as string;

    if (ch === '#') {
      stream.skipToEnd();
      return 'shComment';
    }

    if (ch === "'") {
      stream.next();
      // Single quotes have no escapes at all — that is the whole point of
      // them, and it is also why the mission checks use them.
      while (!stream.eol() && stream.next() !== "'") {
        /* consume */
      }
      state.command = false;
      return 'shString';
    }

    if (ch === '"') {
      stream.next();
      state.inDouble = true;
      state.command = false;
      return 'shString';
    }

    if (ch === '$') {
      stream.next();
      if (readVariable(stream)) enterSubstitution(state);
      else state.command = false;
      return 'shDollar';
    }

    // `<<EOF`, `<<-EOF`, `<<'EOF'` — open a heredoc and remember how it ends.
    const here = stream.match(/^<<(-?)\s*(['"]?)([A-Za-z_][A-Za-z0-9_]*)\2/) as RegExpMatchArray | null;
    const tag = here?.[3];
    if (tag) {
      state.heredoc = { tag, indented: here?.[1] === '-' };
      state.command = false;
      return 'shOperator';
    }

    const op = stream.match(/^(&&|\|\||;;|>>|<<|>&|2>|&>|[|;&<>=(){}[\]!])/) as RegExpMatchArray | null;
    const text = op?.[1];
    if (text) {
      // The `)` that closes a `$(` hands the double quote back, if there was
      // one. A `)` from a `case` pattern or a function header finds the stack
      // empty and is left alone.
      if (text === ')' && state.subst.length) state.inDouble = state.subst.pop() as boolean;
      if (resetsCommand(text) || text === '(' || text === '{') state.command = true;
      return /^[(){}[\]]$/.test(text) ? 'shPunct' : 'shOperator';
    }

    // `-l`, `--force`. Tagged apart from arguments because in a shell the
    // flags are the part you scan for.
    if (!state.command && stream.match(/^-{1,2}[A-Za-z0-9][A-Za-z0-9_-]*/)) return 'shOption';

    if (stream.match(/^\d+(\.\d+)?\b/)) {
      state.command = false;
      return 'shNumber';
    }

    const word = stream.match(/^[^\s|;&<>()"'$#]+/) as RegExpMatchArray | null;
    const name = word?.[0];
    if (name) {
      const wasCommand = state.command;
      state.command = false;
      if (SHELL_KEYWORDS.has(name)) {
        state.command = SHELL_OPENS_COMMAND.has(name);
        return 'shKeyword';
      }
      if (wasCommand) {
        // `FOO=bar cmd` keeps the next word in command position, the way a
        // shell does, so a prefixed assignment does not swallow the command.
        if (/^[A-Za-z_][A-Za-z0-9_]*=/.test(name)) {
          state.command = true;
          return 'shAssign';
        }
        return SHELL_BUILTINS.has(name) ? 'shBuiltin' : 'shCommand';
      }
      return null;
    }

    stream.next();
    return null;
  },

  blankLine(state) {
    if (!state.heredoc && !state.inDouble) state.command = true;
  },

  copyState: (state) => ({ ...state, subst: [...state.subst], heredoc: state.heredoc && { ...state.heredoc } }),

  languageData: { commentTokens: { line: '#' } },

  // Named rather than inferred, so the mapping from "what the lexer saw" to
  // "what the eye reads" is one table in one place.
  //
  // Every name is prefixed, and that is load-bearing rather than tidy.
  // CodeMirror seeds its token table with twelve CodeMirror-5 aliases —
  // `builtin`, `variable`, `def`, `type`, `property` and friends — and those
  // are looked up *before* the table below, so a lexer that returns
  // `'builtin'` silently gets `variableName.standard` and this file's entry
  // is never consulted. It fails by looking almost right, which is how it
  // survived a first read.
  tokenTable: {
    shShebang: t.meta,
    shComment: t.lineComment,
    shKeyword: t.controlKeyword,
    shBuiltin: t.keyword,
    shCommand: t.function(t.variableName),
    shAssign: t.definition(t.variableName),
    shOption: t.attributeName,
    shDollar: t.variableName,
    shString: t.string,
    shNumber: t.number,
    shOperator: t.operator,
    shPunct: t.punctuation,
  },
};

/** Bash, as far as colour is concerned. */
export const shell: Extension = StreamLanguage.define(shellParser);

/* ── JSON ─────────────────────────────────────────────────────────────────── */

/**
 * JSON, with the one distinction that matters when you are reading a
 * `package.json` to find out why something broke: a key is not a value.
 * Both are strings to a parser; only one of them is the thing you are
 * scanning for.
 *
 * Stateless, unlike the shell: nothing in JSON runs over a line end except a
 * structure this does not need to track.
 */
const jsonParser: StreamParser<null> = {
  name: 'json',
  startState: () => null,

  token(stream) {
    if (stream.eatSpace()) return null;

    if (stream.peek() === '"') {
      stream.next();
      while (!stream.eol()) {
        const ch = stream.next() as string;
        if (ch === '\\') {
          stream.next();
          continue;
        }
        if (ch === '"') break;
      }
      // Look ahead without moving: a colon after this string made it a key.
      // The stream is one line, so a colon on the *next* line is not seen and
      // the key degrades to the string it also is. No formatter writes that.
      return stream.match(/^\s*:/, false) ? 'jsonKey' : 'jsonString';
    }

    if (stream.match(/^-?\d+(\.\d+)?([eE][+-]?\d+)?/)) return 'jsonNumber';
    if (stream.match(/^(true|false)\b/)) return 'jsonBool';
    if (stream.match(/^null\b/)) return 'jsonNull';
    if (stream.match(/^[{}[\]]/)) return 'jsonBracket';
    if (stream.match(/^[:,]/)) return 'jsonSeparator';

    stream.next();
    return 'jsonInvalid';
  },

  languageData: { closeBrackets: { brackets: ['[', '{', '"'] } },

  // Prefixed for the same reason the shell's names are: CodeMirror's twelve
  // CodeMirror-5 aliases are resolved ahead of this table, and `property`
  // — the obvious name for a key — is one of them.
  tokenTable: {
    jsonKey: t.propertyName,
    jsonString: t.string,
    jsonNumber: t.number,
    jsonBool: t.bool,
    jsonNull: t.null,
    jsonBracket: t.bracket,
    jsonSeparator: t.separator,
    jsonInvalid: t.invalid,
  },
};

/** JSON, the subset a config file is. */
export const json: Extension = StreamLanguage.define(jsonParser);

/* ── Which language ───────────────────────────────────────────────────────── */

/**
 * One language per extension, with the content as a second opinion.
 *
 * The extension decides, because it nearly always can. What it cannot decide
 * is the file a mission seeds with no extension at all — a git hook, a bare
 * `deploy`, an installed script on the `PATH` — and guessing those from the
 * name would be wrong as often as right. The shebang is not a guess: a file
 * whose first line says `#!/bin/bash` *is* bash, and that is the same line
 * the kernel reads to decide what to run it with.
 *
 * Anything still unrecognised is edited as plain text, which is what every
 * file here used to be.
 */
export function languageFor(path: string, content: string): Extension[] {
  // Guest paths, so a forward slash is the only separator there is.
  const name = path.slice(path.lastIndexOf('/') + 1);
  const dot = name.lastIndexOf('.');
  // `dot <= 0` rather than `=== -1`: a dotfile's leading dot does not start
  // an extension, so `.env` is a name without one and not a file of type
  // "env".
  const ext = dot <= 0 ? '' : name.slice(dot + 1).toLowerCase();
  switch (ext) {
    case 'js':
    case 'mjs':
    case 'cjs':
      return [javascript()];
    case 'jsx':
      return [javascript({ jsx: true })];
    case 'ts':
      return [javascript({ typescript: true })];
    case 'tsx':
      return [javascript({ jsx: true, typescript: true })];
    case 'md':
      return [markdown()];
    case 'json':
      return [json];
    case 'sh':
    case 'bash':
    case 'zsh':
      return [shell];
    default:
      return /^#!.*\b(ba|z|da|k)?sh\b/.test(content) ? [shell] : [];
  }
}
